import clsx from "clsx";

import type { DatepickerTriggerProps } from "../types";

import { CalenderIcon, CrossIcon } from "../../Icons";
import { Typo } from "../../Typo";

export type FieldTriggerProps = DatepickerTriggerProps & {
  allowClear?: boolean;
  placeholder?: string;
  /** Preset name to show instead of the raw dates — see `getShortcutButtonLabel`. */
  shortcutButtonLabel?: string;
};

/**
 * The "field"-style date-range trigger: a tonal pill showing either the matched
 * shortcut name ("Last 30 days") or the formatted range, rather than an editable
 * text input.
 *
 * Shared by `Datepicker`'s `displayVariant="field"` and by callsites that talk
 * to `DatepickerV2` directly, so both render the identical control.
 */
export const FieldTrigger = ({
  allowClear,
  clear,
  close,
  disabled,
  inputText,
  isEmpty,
  isOpen,
  open,
  placeholder,
  shortcutButtonLabel,
}: FieldTriggerProps) => {
  const showClear = allowClear && !isEmpty && !disabled;
  const iconClasses = clsx(
    "h-4 w-4 shrink-0",
    disabled ? "text-icon-disabled" : "text-icon-tertiary",
  );

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={isOpen ? close : open}
      className={clsx(
        "flex h-10 w-full min-w-[260px] items-center gap-3 rounded-lg px-4 outline-none transition-colors duration-150 ease-out",
        disabled
          ? "cursor-not-allowed bg-surface-disabled"
          : "bg-surface-tonal-default hover:bg-surface-tonal-bold",
        isOpen && !disabled && "ring-2 ring-stroke-focused",
      )}
    >
      <Typo
        element="span"
        type="bodyMD"
        textColor={disabled ? "text-text-disabled" : "text-text-primary"}
        className="mr-auto truncate"
      >
        {shortcutButtonLabel || inputText || placeholder}
      </Typo>
      {showClear ? (
        <CrossIcon
          className={iconClasses}
          aria-hidden="true"
          focusable="false"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            clear();
          }}
        />
      ) : (
        <CalenderIcon
          className={iconClasses}
          aria-hidden="true"
          focusable="false"
        />
      )}
    </button>
  );
};

export default FieldTrigger;
